import { z } from "zod";
import { REVIEW_PARTICIPANT_STATUSES } from "@/features/reviews/review-vocabulary";

/// The composed views on a review (FR-006). A single detail page renders only
/// the tabs the caller is entitled to: `dashboard` (every personal + team
/// widget in one place), and the admin `participants` roster + `configuration`.
/// URL-addressable so create-cycle can land on Configuration and deep links can
/// target a specific tab.
export const CYCLE_DETAIL_TABS = ["dashboard", "participants", "configuration"] as const;
export type CycleDetailTab = (typeof CYCLE_DETAIL_TABS)[number];

/// Legacy deep links: the old `mine` and `team` tabs merged into `dashboard`.
const LEGACY_TAB_MAP: Record<string, CycleDetailTab> = {
  mine: "dashboard",
  team: "dashboard",
};

export const REVIEW_CYCLE_DETAIL_LENSES = ["all", "attention"] as const;
export type ReviewCycleDetailLens = (typeof REVIEW_CYCLE_DETAIL_LENSES)[number];

export const REVIEW_CYCLE_DETAIL_GROUPS = ["none", "hierarchy"] as const;
export type ReviewCycleDetailGroup = (typeof REVIEW_CYCLE_DETAIL_GROUPS)[number];

export const REVIEW_CYCLE_DETAIL_RATINGS = ["1", "2", "3", "4", "5"] as const;
export type ReviewCycleDetailRating = (typeof REVIEW_CYCLE_DETAIL_RATINGS)[number];

const participantStatusSchema = z.enum(REVIEW_PARTICIPANT_STATUSES);

const reviewCycleDetailStatusListSchema = z
  .union([participantStatusSchema, z.array(participantStatusSchema)])
  .optional()
  .transform((value) => {
    if (value == null) return undefined;
    return Array.isArray(value) ? value : [value];
  });

export const reviewCycleDetailSearchSchema = z
  .object({
    // No default: an absent tab resolves per persona at render time
    // (resolveActiveCycleDetailTab), so members land on "dashboard" and admins
    // on "participants". Legacy mine/team bookmarks map to dashboard; catch()
    // tolerates junk values.
    tab: z
      .string()
      .optional()
      .transform((value) => {
        if (value == null) return undefined;
        const mapped = LEGACY_TAB_MAP[value] ?? value;
        // reason: the includes() check on the line above proves membership; TS
        // cannot narrow a string via readonly-array includes.
        return (CYCLE_DETAIL_TABS as readonly string[]).includes(mapped)
          ? (mapped as CycleDetailTab)
          : undefined;
      })
      .catch(undefined),
    lens: z.enum(REVIEW_CYCLE_DETAIL_LENSES).default("all").catch("all"),
    status: reviewCycleDetailStatusListSchema.catch(undefined),
    rating: z.enum(REVIEW_CYCLE_DETAIL_RATINGS).optional().catch(undefined),
    group: z.enum(REVIEW_CYCLE_DETAIL_GROUPS).default("none").catch("none"),
  })
  .strict();
export type ReviewCycleDetailSearch = z.infer<typeof reviewCycleDetailSearchSchema>;
